import { useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, CheckCircle2, AlertTriangle, Loader2, Upload, IdCard,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Identity-verification card shown on the patient dashboard + checkout.
 *
 * In-house name check: the patient uploads a photo of their government ID and
 * enters the ID number (driver's license / state ID / passport). The
 * prescribing clinician performs the final identity match during eRx
 * submission, so the patient can continue through intake + payment as
 * soon as the ID is on file. The separate video-attestation step is
 * handled elsewhere and untouched here.
 */
const ID_TYPES = [
  { value: "drivers_license", label: "Driver's license" },
  { value: "state_id", label: "State ID" },
  { value: "passport", label: "Passport" },
];

export default function IdVerificationCard({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [idType, setIdType] = useState("drivers_license");
  const [idNumber, setIdNumber] = useState("");
  const [issuingState, setIssuingState] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await API.get("/me/id-verification/status");
      setStatus(data);
      onStatusChange?.(data);
      return data;
    } catch {
      const fallback = { verified: false, has_id_card: false, verification_required: true };
      setStatus(fallback);
      onStatusChange?.(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!file) { toast.error("Attach a photo of your ID"); return; }
    const num = idNumber.trim();
    if (num.length < 4) { toast.error("Enter your ID number"); return; }
    if (idType !== "passport" && issuingState.trim().length !== 2) {
      toast.error("Enter the 2-letter issuing state (e.g. TX)"); return;
    }
    const fd = new FormData();
    fd.append("id_type", idType);
    fd.append("id_number", num);
    fd.append("id_issuing_state", idType === "passport" ? "" : issuingState.trim().toUpperCase());
    fd.append("photo", file);
    setSubmitting(true);
    try {
      await API.post("/me/id-verification/id-card", fd);
      toast.success("ID submitted — our care team will review it shortly.");
      setFile(null); setIdNumber(""); setIssuingState("");
      if (fileRef.current) fileRef.current.value = "";
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload your ID. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;
  const verified = !!status?.verified;
  const submitted = !verified && !!status?.has_id_card;

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-4" data-testid="id-verification-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-5 h-5 ${verified ? "text-emerald-500" : "text-amber-500"}`} />
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">Identity Verification</h3>
        </div>
        {verified ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" data-testid="id-verification-verified-badge">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
          </Badge>
        ) : submitted ? (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300" data-testid="id-verification-pending-badge">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Pending review
          </Badge>
        ) : (
          <Badge className="bg-rose-100 text-rose-700 border-rose-300" data-testid="id-verification-required-badge">
            <AlertTriangle className="w-3 h-3 mr-1" /> Upload required to unlock checkout
          </Badge>
        )}
      </div>

      {verified ? (
        <p className="text-xs text-emerald-700 dark:text-emerald-400" data-testid="id-verification-done-text">
          Your identity was verified{status?.verified_at ? ` on ${new Date(status.verified_at).toLocaleDateString()}` : ""}. You&apos;re all set.
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-4">
            To confirm you&apos;re the right person for a prescription, upload a clear photo of your
            government-issued ID and enter the details below. Your prescriber verifies the ID during prescription review — you can continue through intake and checkout as soon as it&apos;s uploaded.
          </p>

          {submitted && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mb-4" data-testid="id-verification-pending-text">
              Your ID{status?.id_number_last4 ? ` ending in ${status.id_number_last4}` : ""} was submitted
              {status?.id_photo_uploaded_at ? ` on ${new Date(status.id_photo_uploaded_at).toLocaleDateString()}` : ""}.
              You&apos;re cleared to keep going — your prescriber verifies it during prescription review.
              Re-upload below if you need to replace it.
            </p>
          )}

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-600 dark:text-slate-300">ID type</Label>
                <select
                  value={idType}
                  onChange={(e) => setIdType(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 py-2"
                  data-testid="id-verification-type-select"
                >
                  {ID_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-600 dark:text-slate-300">ID number</Label>
                <Input
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="License / passport number"
                  className="mt-1 rounded-lg text-sm"
                  data-testid="id-verification-number-input"
                />
              </div>
              {idType !== "passport" && (
                <div>
                  <Label className="text-xs text-slate-600 dark:text-slate-300">Issuing state</Label>
                  <Input
                    value={issuingState}
                    onChange={(e) => setIssuingState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder=""
                    maxLength={2}
                    className="mt-1 rounded-lg text-sm uppercase"
                    data-testid="id-verification-state-input"
                  />
                </div>
              )}
            </div>

            <label className="block rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-4 text-center cursor-pointer hover:border-purple-300 transition-colors" data-testid="id-verification-dropzone">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="id-verification-file-input"
              />
              <IdCard className="w-6 h-6 text-purple-500 mx-auto mb-1.5" />
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{file ? file.name : "Choose a photo of your ID"}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">JPG, PNG, HEIC or PDF · up to 8 MB</p>
            </label>

            <Button
              onClick={submit}
              disabled={submitting || !file}
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="submit-id-verification-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {submitted ? "Re-upload ID" : "Submit ID"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
